// apps/configurator/src/stores/submissionStore.ts
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Submission, QuoteResult, computeQuote } from '@gorampe/calc-engine';
import { Catalog } from '@gorampe/catalog-adapter';

interface SubmissionState {
  submission: Submission;
  quote: QuoteResult | null;
  catalog: Catalog | null;
  setCatalog: (catalog: Catalog) => void;
  patch: (patch: Partial<Submission>) => void;
  patchConfig: (configPatch: Partial<Submission['config']>) => void;
  patchOptions: (optionsPatch: Partial<Submission['options']>) => void;
  patchMeta: (metaPatch: Partial<Submission['meta']>) => void;
  reset: () => void;
}

// crypto.randomUUID() requires a secure context (HTTPS or localhost). On HTTP over
// a LAN IP (e.g. http://192.168.x.x), iOS Safari throws. Use a fallback that works
// in any context — sufficient for local-IDs, not cryptographic.
function generateSubmissionId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through to fallback */
  }
  return 'sub-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 11);
}

const blankSubmission = (): Submission => ({
  id: generateSubmissionId(),
  meta: { client: '', project: '', date: new Date().toISOString().slice(0, 10) },
  config: {
    usage: 'res',
    heightIn: 24,
    widthIn: 36,
    floor: 'plein',
    railType: 'barrotin',
    shape: { kind: 'straight', totalRunIn: 288 },
  },
  options: {
    rental: { enabled: false, durationMonths: 0 },
    installation: { enabled: false, transportKm: 0 },
    uninstallation: { enabled: false },
    accessories: [],
    passeSeuils: [],
    taxRegion: 'qc',
  },
  manualLines: [],
});

function recompute(submission: Submission, catalog: Catalog | null): QuoteResult | null {
  if (!catalog) return null;
  try {
    return computeQuote({ submission, catalog });
  } catch (err) {
    console.error('computeQuote failed', err);
    return null;
  }
}

export const useSubmissionStore = create<SubmissionState>()(
  persist(
    (set, get) => ({
      submission: blankSubmission(),
      quote: null,
      catalog: null,
      setCatalog: (catalog) => {
        const quote = recompute(get().submission, catalog);
        set({ catalog, quote });
      },
      patch: (patch) => {
        const sub = { ...get().submission, ...patch };
        set({ submission: sub, quote: recompute(sub, get().catalog) });
      },
      patchConfig: (configPatch) => {
        const sub = {
          ...get().submission,
          config: { ...get().submission.config, ...configPatch },
        };
        set({ submission: sub, quote: recompute(sub, get().catalog) });
      },
      patchOptions: (optionsPatch) => {
        const sub = {
          ...get().submission,
          options: { ...get().submission.options, ...optionsPatch },
        };
        set({ submission: sub, quote: recompute(sub, get().catalog) });
      },
      patchMeta: (metaPatch) => {
        const sub = {
          ...get().submission,
          meta: { ...get().submission.meta, ...metaPatch },
        };
        set({ submission: sub, quote: recompute(sub, get().catalog) });
      },
      reset: () => {
        const fresh = blankSubmission();
        set({ submission: fresh, quote: recompute(fresh, get().catalog) });
      },
    }),
    {
      name: 'gorampe-submission',
      partialize: (s) => ({ submission: s.submission }),
    },
  ),
);
